import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Select,
  MenuItem,
  Grid,
  Divider
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Notifications as NotificationsIcon,
  Translate as TranslateIcon,
  WifiOff as WifiOffIcon
} from '@mui/icons-material';
import { useState } from 'react';
import { useTheme as useCustomTheme } from '../../../../contexts/useTheme';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { useTranslation } from 'react-i18next';

export const ApplicationSettings = () => {
  const { mode, setTheme } = useCustomTheme();
  const { t } = useTranslation();
  const [pushNotifications, setPushNotifications] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [language, setLanguage] = useState('english');
  const [timezone, setTimezone] = useState('utc');
  const isOnline = useOnlineStatus();

  if (!isOnline) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          textAlign: 'center',
          p: 4
        }}
      >
        <WifiOffIcon
          sx={{
            fontSize: 80,
            color: 'text.disabled',
            opacity: 0.5,
            mb: 2
          }}
        />
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
          {t('settings:offline.title.applicationSettings')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 500 }}
        >
          {t('settings:offline.message.applicationSettings')}
        </Typography>
      </Box>
    );
  }

  const handleThemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newTheme = event.target.value as 'light' | 'dark';
    setTheme(newTheme);
  };

  return (
    <Box
      sx={{
        maxWidth: 800,
        width: '100%',
        mx: 'auto', // Center the content
        pb: 4 // Add padding bottom
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          {t('settings:titles.applicationSettings')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('settings:descriptions.applicationSettings')}
        </Typography>
      </Box>

      {/* Theme & Appearance Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <PaletteIcon sx={{ mr: 2, color: 'text.secondary' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('settings:application.theme.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settings:application.theme.description')}
              </Typography>
            </Box>
          </Box>

          <FormControl component="fieldset">
            <FormLabel
              component="legend"
              sx={{
                fontWeight: 500,
                mb: 2,
                '&.Mui-focused': {
                  color: 'text.primary'
                }
              }}
            >
              {t('settings:application.theme.colorTheme')}
            </FormLabel>
            <RadioGroup
              value={mode}
              onChange={handleThemeChange}
              sx={{ gap: 1 }}
            >
              <FormControlLabel
                value="light"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: '#fff',
                        border: '1px solid #ddd'
                      }}
                    />
                    {t('settings:application.theme.light')}
                  </Box>
                }
              />
              <FormControlLabel
                value="dark"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: '#333'
                      }}
                    />
                    {t('settings:application.theme.dark')}
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Notifications Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <NotificationsIcon sx={{ mr: 2, color: 'text.secondary' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('settings:application.notifications.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settings:application.notifications.description')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {t('settings:application.notifications.push.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings:application.notifications.push.description')}
                </Typography>
              </Box>
              <Switch
                checked={pushNotifications}
                onChange={e => setPushNotifications(e.target.checked)}
                color="primary"
                disabled={true}
              />
            </Box>

            <Divider sx={{ mx: -4 }} />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {t('settings:application.notifications.email.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('settings:application.notifications.email.description')}
                </Typography>
              </Box>
              <Switch
                checked={emailNotifications}
                onChange={e => setEmailNotifications(e.target.checked)}
                color="primary"
                disabled={true}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Language & Region Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <TranslateIcon sx={{ mr: 2, color: 'text.secondary' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('settings:application.language.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settings:application.language.description')}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {t('settings:application.language.languageLabel')}
                </Typography>
                <Select
                  value={language}
                  onChange={e => setLanguage(e.target.value)}
                  displayEmpty
                  disabled={true}
                >
                  <MenuItem value="english">
                    {t('settings:application.language.languages.english')}
                  </MenuItem>
                  <MenuItem value="spanish">
                    {t('settings:application.language.languages.spanish')}
                  </MenuItem>
                  <MenuItem value="french">
                    {t('settings:application.language.languages.french')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {t('settings:application.language.timezoneLabel')}
                </Typography>
                <Select
                  value={timezone}
                  onChange={e => setTimezone(e.target.value)}
                  displayEmpty
                  disabled={true}
                >
                  <MenuItem value="utc">
                    {t('settings:application.language.timezones.utc')}
                  </MenuItem>
                  <MenuItem value="est">
                    {t('settings:application.language.timezones.est')}
                  </MenuItem>
                  <MenuItem value="pst">
                    {t('settings:application.language.timezones.pst')}
                  </MenuItem>
                  <MenuItem value="cst">
                    {t('settings:application.language.timezones.cst')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};
